import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { useSupabaseClient } from 'src/api/supabase'
import { useOrganizationCurrency } from 'src/shared/hooks/useOrganizationCurrency'
import { convertFromSEK } from 'src/shared/utils/currency'
import { Database, type Json } from 'supabase/database.types'
import type { QuoteProofLinkRow } from 'src/shared/utils/proofUrls'

import { queryKeys } from './queryKeys'
import { filterActiveQuoteLines } from 'src/shared/utils/quoteAlternatives'
import { trackEvent } from './apiAnalytics'
import { QuoteItem_ } from './apiQuoteItem'
import { Design_ } from './apiDesigns'
import { generateUniqueId } from 'src/shared/utils'
import type { QuoteItemThumbnailSource } from 'src/shared/utils/quoteItemThumbnail'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'
import {
  DELIVERED_STATUS,
  ACTIVE_QUOTE_STATUSES,
} from 'src/shared/consts/quoteStatus'

export type InsertQuote = Database['public']['Tables']['quote']['Insert']
export type QuoteOrganizationPreview = {
  name: string
  currency?: string | null
  org_number?: string | null
  vat_number?: string | null
  billing_email?: string | null
  billing_address_line1?: string | null
  billing_address_line2?: string | null
  billing_postcode?: string | null
  billing_city?: string | null
  billing_country?: string | null
  default_invoice_reference?: string | null
}

export type Quote_ = Database['public']['Tables']['quote']['Row'] & {
  quote_item?: (QuoteItem_ & {
    design?: Design_[]
  })[]
} & {
  organization?: QuoteOrganizationPreview
  quote_proof?: QuoteProofLinkRow[]
}

/** List query shape: thumbnails + the fields needed to sum order value. */
export type QuoteListItemPreview = {
  id: number
  slot?: string | null
  is_recommended?: boolean | null
  customer_selected_at?: string | null
  product_variant_color_id?: number | null
  /** Direct color join used by list queries (avoids product→variant fan-out). */
  product_variant_color?: QuoteItemThumbnailSource['product_variant_color']
  product?: QuoteItemThumbnailSource['product']
  quantity?: number | null
  unit_price?: number | null
  discount?: number | null
  quote_sub_item?: Array<{
    unit_price?: number | null
    quantity?: number | null
  }>
}

export type QuoteListConversationPreview = {
  order_message?: Array<{ id: number }> | null
}

export type QuoteListRow = Database['public']['Tables']['quote']['Row'] & {
  quote_item?: QuoteListItemPreview[]
  organization?: { name: string }
  quote_proof?: QuoteProofLinkRow[]
  conversation?: QuoteListConversationPreview[] | null
}

/** Proposal alternatives stay on the quote; only the active line per slot is counted. */
export const getQuoteItemCount = (quote: QuoteListRow) =>
  filterActiveQuoteLines(quote.quote_item).length

export const getQuoteMessageCount = (quote: QuoteListRow) =>
  (quote.conversation ?? []).reduce(
    (sum, conversation) => sum + (conversation.order_message?.length ?? 0),
    0
  )

/** Same formula as the quote table: main lines + decoration / setup / extra lines. */
export const getQuoteOrderValue = (quote: QuoteListRow) =>
  filterActiveQuoteLines(quote.quote_item).reduce((sum, item) => {
    const main = Math.max(
      0,
      (item.unit_price || 0) * (item.quantity || 0) - (item.discount || 0)
    )
    const extras = (item.quote_sub_item ?? []).reduce(
      (extraSum, sub) =>
        extraSum + (sub.unit_price || 0) * (sub.quantity || 0),
      0
    )
    return sum + main + extras
  }, 0)

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const buildQuoteSearchOrFilter = (term: string) => {
  const safe = term.replace(/[%_,]/g, '')
  if (!safe) return null
  const parts = [
    `name.ilike.%${safe}%`,
    `public_id.ilike.%${safe}%`,
    `organization.name.ilike.%${safe}%`,
  ]
  if (UUID_RE.test(safe)) {
    parts.push(`org_id.eq.${safe}`)
  }
  return parts.join(',')
}

const QUOTE_PROOF_SELECT = `quote_proof (
  proof_id,
  status,
  sort_order,
  linked_at,
  customer_comment,
  proof ( id, url, created_at )
)`

type QuoteFetchRow = Database['public']['Tables']['quote']['Row'] & {
  organization?: QuoteOrganizationPreview
  quote_item?: Array<{
    product?: { price?: number | null } | null
    [key: string]: unknown
  }>
}

const getQuoteCurrency = (row: QuoteFetchRow | null) =>
  row?.organization?.currency ?? 'SEK'

const convertFetchedQuoteItems = (
  items: QuoteFetchRow['quote_item'],
  targetCurrency: string
) =>
  (items ?? []).map((item) =>
    item.product?.price != null
      ? {
          ...item,
          product: {
            ...item.product,
            price: convertFromSEK(item.product.price, targetCurrency) ?? 0,
          },
        }
      : item
  )

const mapFetchedQuote = (data: QuoteFetchRow): Quote_ => {
  const targetCurrency = getQuoteCurrency(data)
  return {
    ...data,
    quote_item: convertFetchedQuoteItems(
      data.quote_item,
      targetCurrency
    ) as Quote_['quote_item'],
  }
}

export type QuoteListQueryOptions = {
  /** Max rows (applied after filters). Unscoped admin lists default to 100. */
  limit?: number
  /** 0-based offset for pagination */
  offset?: number
  /** Include only these statuses */
  statuses?: string[]
  /** Exclude these statuses */
  excludeStatuses?: string[]
  /** Exclude rows with null/empty status (drafts that never set status) */
  excludeNullStatus?: boolean
  /** Extra org filter (admin cross-org lists) */
  filterOrgId?: string
  /** ilike on name, public_id, org name; exact match on org_id when term is a UUID */
  search?: string
  /** Nested quote_proof join — expensive; default false */
  includeProofs?: boolean
  /** Nested product images for card thumbnails; default true */
  includeItemThumbnails?: boolean
  /**
   * Count strategy. Prefer `none` — exact counts scan the full filtered set
   * and often time out with nested selects. Default: none.
   */
  countMode?: 'exact' | 'estimated' | 'none'
}

const ADMIN_UNSCOPED_DEFAULT_LIMIT = 100
const ORG_SCOPED_DEFAULT_LIMIT = 100

/** Direct PVC join — one color's images per line item (not every variant). */
const QUOTE_LIST_ITEM_THUMBNAIL_FIELDS = `id, slot, is_recommended, customer_selected_at, product_variant_color_id, quantity, unit_price, discount, product_variant_color!quote_item_product_variant_color_id_fkey(
  id,
  product_image(image_url, order)
), quote_sub_item(unit_price, quantity)`

const buildQuoteListSelect = ({
  includeProofs,
  includeItemThumbnails,
}: {
  includeProofs: boolean
  includeItemThumbnails: boolean
}) => {
  const itemSelect = includeItemThumbnails
    ? `quote_item!quote_item_quote_id_fkey(${QUOTE_LIST_ITEM_THUMBNAIL_FIELDS})`
    : `quote_item!quote_item_quote_id_fkey(id, slot, is_recommended, customer_selected_at)`

  const proofs = includeProofs ? `,\n            ${QUOTE_PROOF_SELECT}` : ''

  return `id, name, public_id, status, org_id, created_at,
            organization(name),
            ${itemSelect}${proofs}`
}

export const useQuoteApi = ({
  org_id,
  skipListQuery = false,
  list,
}: {
  org_id?: string
  /** When true, list query is disabled (mutations only). */
  skipListQuery?: boolean
  /** Optional list filters / pagination (esp. for unscoped admin). */
  list?: QuoteListQueryOptions
}) => {
  const queryClient = useQueryClient()
  const supabase = useSupabaseClient()
  const isAdmin = useIsAdmin()
  const currency = useOrganizationCurrency()

  const isUnscopedAdmin = isAdmin && !org_id
  const limit =
    list?.limit ??
    (isUnscopedAdmin
      ? ADMIN_UNSCOPED_DEFAULT_LIMIT
      : ORG_SCOPED_DEFAULT_LIMIT)
  const offset = list?.offset ?? 0
  const includeProofs = list?.includeProofs ?? false
  const includeItemThumbnails = list?.includeItemThumbnails ?? true
  const countMode = list?.countMode ?? 'none'

  const { data: quotes, isPending: isLoadingQuotes } = useQuery({
    queryKey: [
      queryKeys.quotes,
      org_id,
      currency,
      limit,
      offset,
      list?.statuses,
      list?.excludeStatuses,
      list?.excludeNullStatus,
      list?.filterOrgId,
      list?.search,
      includeProofs,
      includeItemThumbnails,
      countMode,
    ],
    queryFn: async () => {
      const countOption =
        countMode === 'none'
          ? undefined
          : ({ count: countMode } as const)

      let query = supabase
        .from('quote')
        .select(
          buildQuoteListSelect({ includeProofs, includeItemThumbnails }),
          countOption
        )
        .order('created_at', { ascending: false })

      if (org_id) {
        query = query.eq('org_id', org_id)
      }
      if (list?.filterOrgId) {
        query = query.eq('org_id', list.filterOrgId)
      }
      if (list?.statuses?.length) {
        query = query.in('status', list.statuses)
      }
      if (list?.excludeStatuses?.length) {
        for (const status of list.excludeStatuses) {
          query = query.neq('status', status)
        }
      }
      if (list?.excludeNullStatus) {
        query = query.not('status', 'is', null)
      }
      if (list?.search?.trim()) {
        const orFilter = buildQuoteSearchOrFilter(list.search.trim())
        if (orFilter) query = query.or(orFilter)
      }
      if (limit != null) {
        query = query.range(offset, offset + limit - 1)
      }

      const { data, error, count } = await query

      if (error) throw error

      // Dynamic select string — supabase-js can't infer the shape.
      const rows = (data ?? []) as unknown as QuoteListRow[]
      return {
        data: rows,
        count: count ?? null,
        hasMore: limit != null ? rows.length === limit : false,
      }
    },
    enabled: !skipListQuery && (!!org_id || isAdmin),
  })

  const createQuote = useMutation({
    mutationFn: async (insertQuote: InsertQuote) => {
      const quoteResult = await supabase
        .from('quote')
        .insert(insertQuote)
        .select('id, public_id')
        .single()

      if (quoteResult.error) {
        throw quoteResult.error
      }

      const quoteId = quoteResult.data.id

      // Generate public_id for the quote
      const quotePublicId = generateUniqueId(quoteId)

      // Update the quote with generated public_id
      const res = await supabase
        .from('quote')
        .update({
          public_id: quotePublicId,
        })
        .eq('id', quoteId)
        .select('id, public_id')
        .single()

      return res.data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.quotes] })
      queryClient.invalidateQueries({ queryKey: [queryKeys.orders] })
      if (data?.id) {
        trackEvent(supabase, {
          name: 'request_created',
          quoteId: data.id,
        })
      }
      return data
    },
  })

  const updateQuote = useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: InsertQuote & { id: number }) => {
      if (id == null) throw new Error('Quote id is required')
      return await supabase.from('quote').update(updates).eq('id', id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.quotes] })
    },
  })

  // Customer approval of a sales-approved proposal. Goes through an RPC so a
  // link-holder on /share-order (not an org member) can approve without
  // having UPDATE rights on the quote row. When the customer chose between
  // alternatives (quote_item.slot) or changed quantities, the selection RPC
  // records the chosen line per slot (keeping every alternative) and writes the
  // quantities in the same transaction.
  const approveSharedQuote = useMutation({
    mutationFn: async ({
      public_id,
      quote_id,
      selected_item_ids,
      quantities,
      has_alternatives = false,
    }: {
      public_id: string
      quote_id: number
      selected_item_ids?: number[]
      /** quote_item id → quantity, only for lines whose quantity changed. */
      quantities?: Record<number, number>
      /**
       * The quote has slotted lines. Then approval always goes through the
       * selection RPC, which refuses unless exactly one line per slot is
       * chosen, so a missing choice can't slip through the plain approval.
       */
      has_alternatives?: boolean
    }) => {
      const hasQuantities = Object.keys(quantities ?? {}).length > 0
      const { data, error } =
        has_alternatives || selected_item_ids?.length || hasQuantities
        ? await supabase.rpc('approve_shared_quote_with_selection', {
            p_public_id: public_id,
            p_quote_id: quote_id,
            p_selected_item_ids: selected_item_ids ?? [],
            p_quantities: quantities ?? {},
          })
        : await supabase.rpc('approve_shared_quote', {
            p_public_id: public_id,
            p_quote_id: quote_id,
          })
      if (error) throw error
      return { id: data }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.quotes] })
      queryClient.invalidateQueries({ queryKey: [queryKeys.quoteItems] })
    },
  })

  const updatePublicQuote = useMutation({
    mutationFn: async ({
      public_id,
      org_id,
    }: {
      public_id: string
      org_id: string
    }) => {
      // Moves quote + chat + conversation in one step; RLS does not let a
      // link-holder update the quote row directly.
      const { data, error } = await supabase.rpc('claim_shared_quote', {
        p_public_id: public_id,
        p_org_id: org_id,
      })

      if (error) throw error

      return { id: data }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.quotes] })
      queryClient.invalidateQueries({ queryKey: [queryKeys.chats] })
      queryClient.invalidateQueries({ queryKey: [queryKeys.conversations] })
    },
  })

  const deleteQuote = useMutation({
    mutationFn: async (id: number) => {
      const { data, error } = await supabase
        .from('quote')
        .delete()
        .eq('id', id)
        .select('id')
      if (error) throw error
      if (!data?.length) throw new Error('Quote could not be deleted')
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.quotes] })
    },
  })

  return {
    quotes,
    isLoadingQuotes,
    updateQuote,
    updatePublicQuote,
    approveSharedQuote,
    deleteQuote,
    createQuote,
  }
}

export const useSaveQuoteCanvasData = (publicId?: string) => {
  const supabase = useSupabaseClient()
  const queryClient = useQueryClient()

  return useCallback(
    async (quoteId: number, canvasData: Json) => {
      const { error } = await supabase
        .from('quote')
        .update({ canvas_data: canvasData })
        .eq('id', quoteId)
      if (error) throw error

      if (!publicId) return
      queryClient.setQueryData(
        [queryKeys.quotes, 'byId', publicId],
        (old: Quote_ | null | undefined) =>
          old ? { ...old, canvas_data: canvasData } : old
      )
    },
    [publicId, queryClient, supabase]
  )
}

/** Lightweight admin jump search — no nested items/proofs. */
export const useAdminQuoteJumpSearch = (search: string) => {
  const supabase = useSupabaseClient()
  const term = search.trim()

  return useQuery({
    queryKey: [queryKeys.quotes, 'admin-jump', term],
    queryFn: async () => {
      const orFilter = buildQuoteSearchOrFilter(term)
      if (!orFilter) return []

      const { data, error } = await supabase
        .from('quote')
        .select('id, name, public_id, status, org_id, organization(name)')
        .or(orFilter)
        .order('created_at', { ascending: false })
        .limit(8)

      if (error) throw error
      return (data ?? []) as QuoteListRow[]
    },
    enabled: term.length >= 2,
  })
}

export type AdminQuoteListFilters = {
  statuses?: string[]
  /** Also match rows with null/empty status (for build-quote column). */
  includeNullStatus?: boolean
  orgId?: string
  quoteId?: number
  search?: string
  excludeAnon?: boolean
  hasItemsOnly?: boolean
  /** Only quotes with no org (lead proposals). */
  unassignedOnly?: boolean
  /** Only landing-page lead-magnet quotes (`quote.source = lead_magnet`). */
  leadMagnetOnly?: boolean
}

export type AdminPaginatedQuotesParams = AdminQuoteListFilters & {
  page: number
  pageSize: number
  enabled?: boolean
}

type AdminQuotesPageResult = {
  data: QuoteListRow[]
  hasMore: boolean
  page: number
  pageSize: number
}

type SupabaseClient = ReturnType<typeof useSupabaseClient>

const applyAdminQuoteFilters = <T extends { or: Function; in: Function; is: Function; eq: Function; neq: Function; not: Function }>(
  query: T,
  {
    statuses,
    includeNullStatus = false,
    orgId,
    quoteId,
    search,
    excludeAnon = true,
    unassignedOnly = false,
    leadMagnetOnly = false,
  }: AdminQuoteListFilters
): T => {
  let next = query

  if (includeNullStatus && statuses?.length) {
    const statusList = statuses.map((s) => `"${s}"`).join(',')
    next = next.or(`status.in.(${statusList}),status.is.null`)
  } else if (statuses?.length) {
    next = next.in('status', statuses)
  } else if (includeNullStatus) {
    next = next.is('status', null)
  }
  if (unassignedOnly) {
    next = next.is('org_id', null)
  } else {
    next = next.not('org_id', 'is', null)
    if (orgId) {
      next = next.eq('org_id', orgId)
    }
    if (leadMagnetOnly) {
      next = next.eq('source', 'lead_magnet')
    } else if (excludeAnon) {
      next = next.neq('organization.name', 'Anon')
    }
  }
  if (quoteId != null) {
    next = next.eq('id', quoteId)
  }
  if (search?.trim()) {
    const orFilter = buildQuoteSearchOrFilter(search.trim())
    if (orFilter) next = next.or(orFilter)
  }

  return next
}

const adminQuoteOrgSelect = ({
  excludeAnon,
  unassignedOnly,
  leadMagnetOnly,
}: {
  excludeAnon: boolean
  unassignedOnly: boolean
  leadMagnetOnly: boolean
}) =>
  unassignedOnly || !excludeAnon || leadMagnetOnly
    ? 'organization(name)'
    : 'organization!inner(name)'

const fetchAdminQuotesPage = async (
  supabase: SupabaseClient,
  {
    page,
    pageSize,
    statuses,
    includeNullStatus = false,
    orgId,
    quoteId,
    search,
    excludeAnon = true,
    hasItemsOnly = false,
    unassignedOnly = false,
    leadMagnetOnly = false,
  }: AdminPaginatedQuotesParams
): Promise<AdminQuotesPageResult> => {
  const from = page * pageSize
  const to = from + pageSize // inclusive → pageSize + 1 rows

  const orgSelect = adminQuoteOrgSelect({
    excludeAnon,
    unassignedOnly,
    leadMagnetOnly,
  })
  const itemSelect = hasItemsOnly
    ? `quote_item!inner(${QUOTE_LIST_ITEM_THUMBNAIL_FIELDS})`
    : `quote_item(${QUOTE_LIST_ITEM_THUMBNAIL_FIELDS})`

  const query = applyAdminQuoteFilters(
    supabase
      .from('quote')
      .select(
        `id, name, public_id, status, org_id, created_at, ${orgSelect}, ${itemSelect},
          conversation(order_message(id))`
      )
      .order('created_at', { ascending: false })
      .range(from, to),
    {
      statuses,
      includeNullStatus,
      orgId,
      quoteId,
      search,
      excludeAnon,
      unassignedOnly,
      leadMagnetOnly,
    }
  )

  const { data, error } = await query
  if (error) throw error

  const rows = (data ?? []) as QuoteListRow[]
  const hasMore = rows.length > pageSize

  return {
    data: hasMore ? rows.slice(0, pageSize) : rows,
    hasMore,
    page,
    pageSize,
  }
}

const fetchAdminQuoteCount = async (
  supabase: SupabaseClient,
  {
    statuses,
    includeNullStatus = false,
    orgId,
    quoteId,
    search,
    excludeAnon = true,
    hasItemsOnly = false,
    unassignedOnly = false,
    leadMagnetOnly = false,
  }: AdminQuoteListFilters
): Promise<number | undefined> => {
  const orgSelect = adminQuoteOrgSelect({
    excludeAnon,
    unassignedOnly,
    leadMagnetOnly,
  })
  const itemSelect = hasItemsOnly
    ? 'quote_item!inner(id)'
    : 'quote_item(id, slot, is_recommended, customer_selected_at)'

  const query = applyAdminQuoteFilters(
    supabase
      .from('quote')
      .select(`id, ${orgSelect}, ${itemSelect}`, {
        count: 'exact',
        head: true,
      }),
    {
      statuses,
      includeNullStatus,
      orgId,
      quoteId,
      search,
      excludeAnon,
      unassignedOnly,
      leadMagnetOnly,
    }
  )

  const { count, error } = await query
  if (error) {
    console.error('Failed to count admin quotes:', error)
    return undefined
  }
  return count ?? 0
}

/**
 * Server-side paginated quote list for `/admin` surfaces.
 * Fetches `pageSize + 1` rows to detect `hasMore` without an exact count
 * (exact counts time out on large quote tables).
 */
export const useAdminPaginatedQuotes = ({
  page,
  pageSize,
  statuses,
  includeNullStatus,
  orgId,
  quoteId,
  search,
  excludeAnon = true,
  hasItemsOnly = false,
  unassignedOnly = false,
  leadMagnetOnly = false,
  enabled = true,
}: AdminPaginatedQuotesParams) => {
  const supabase = useSupabaseClient()
  const isAdmin = useIsAdmin()

  return useQuery({
    queryKey: [
      queryKeys.quotes,
      'admin-paginated',
      page,
      pageSize,
      statuses,
      includeNullStatus,
      orgId,
      quoteId,
      search,
      excludeAnon,
      hasItemsOnly,
      unassignedOnly,
      leadMagnetOnly,
    ],
    queryFn: () =>
      fetchAdminQuotesPage(supabase, {
        page,
        pageSize,
        statuses,
        includeNullStatus,
        orgId,
        quoteId,
        search,
        excludeAnon,
        hasItemsOnly,
        unassignedOnly,
        leadMagnetOnly,
      }),
    enabled: enabled && isAdmin,
    retry: false,
  })
}

export type AdminInfiniteQuotesParams = AdminQuoteListFilters & {
  pageSize?: number
  enabled?: boolean
}

/** Infinite scroll quote list for admin pipeline columns. */
export const useAdminInfiniteQuotes = ({
  pageSize = 20,
  statuses,
  includeNullStatus = false,
  orgId,
  quoteId,
  search,
  excludeAnon = true,
  hasItemsOnly = false,
  unassignedOnly = false,
  leadMagnetOnly = false,
  enabled = true,
}: AdminInfiniteQuotesParams) => {
  const supabase = useSupabaseClient()
  const isAdmin = useIsAdmin()

  return useInfiniteQuery({
    queryKey: [
      queryKeys.quotes,
      'admin-infinite',
      pageSize,
      statuses,
      includeNullStatus,
      orgId,
      quoteId,
      search,
      excludeAnon,
      hasItemsOnly,
      unassignedOnly,
      leadMagnetOnly,
    ],
    queryFn: ({ pageParam }) =>
      fetchAdminQuotesPage(supabase, {
        page: pageParam,
        pageSize,
        statuses,
        includeNullStatus,
        orgId,
        quoteId,
        search,
        excludeAnon,
        hasItemsOnly,
        unassignedOnly,
        leadMagnetOnly,
      }),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    enabled: enabled && isAdmin,
    retry: false,
  })
}

/** Exact head-count for one pipeline column (respects list filters). */
export const useAdminQuoteStatusCount = ({
  statuses,
  includeNullStatus = false,
  orgId,
  quoteId,
  search,
  excludeAnon = true,
  hasItemsOnly = false,
  unassignedOnly = false,
  leadMagnetOnly = false,
  enabled = true,
}: AdminQuoteListFilters & { enabled?: boolean }) => {
  const supabase = useSupabaseClient()
  const isAdmin = useIsAdmin()

  return useQuery({
    queryKey: [
      queryKeys.quotes,
      'admin-status-count',
      statuses,
      includeNullStatus,
      orgId,
      quoteId,
      search,
      excludeAnon,
      hasItemsOnly,
      unassignedOnly,
      leadMagnetOnly,
    ],
    queryFn: () =>
      fetchAdminQuoteCount(supabase, {
        statuses,
        includeNullStatus,
        orgId,
        quoteId,
        search,
        excludeAnon,
        hasItemsOnly,
        unassignedOnly,
        leadMagnetOnly,
      }),
    enabled: enabled && isAdmin,
    retry: false,
  })
}

export type RequestResponseFilter =
  | 'all'
  | 'needs_response'
  | 'awaiting_response'

type QuoteActivitySnapshot = {
  lastMessageAt: string | null
  lastMessageRole: string | null
  responseState: Exclude<RequestResponseFilter, 'all'> | null
}

const TEAM_PING_MARKER = 'team_ping'

const isTeamPingOrderMessage = (
  role: string | null,
  content: string | null | undefined
) =>
  role === 'system' &&
  (content ?? '').replace(/&quot;/g, '"').includes(TEAM_PING_MARKER)

const getQuoteResponseState = (
  role: string | null,
  content: string | null | undefined
): QuoteActivitySnapshot['responseState'] => {
  if (!role) return null
  if (role === 'admin') return 'awaiting_response'
  if (role === 'customer') return 'needs_response'
  if (isTeamPingOrderMessage(role, content)) return 'needs_response'
  return null
}

const fetchQuoteActivityByQuoteId = async (
  supabase: SupabaseClient
): Promise<Map<number, QuoteActivitySnapshot>> => {
  const { data, error } = await supabase
    .from('order_message')
    .select('quote_id, created_at, role, content')
    .not('quote_id', 'is', null)
    .order('created_at', { ascending: false })

  if (error) throw error

  const map = new Map<number, QuoteActivitySnapshot>()
  for (const row of data ?? []) {
    const quoteId = row.quote_id
    if (quoteId == null || map.has(quoteId)) continue
    map.set(quoteId, {
      lastMessageAt: row.created_at,
      lastMessageRole: row.role,
      responseState: getQuoteResponseState(row.role, row.content),
    })
  }
  return map
}

export type AdminRequestsOverviewParams = {
  page: number
  pageSize: number
  search?: string
  responseFilter?: RequestResponseFilter
  excludeAnon?: boolean
  enabled?: boolean
}

const fetchAdminRequestsOverviewIndex = async (
  supabase: SupabaseClient,
  {
    search,
    responseFilter = 'all',
    excludeAnon = true,
  }: Pick<
    AdminRequestsOverviewParams,
    'search' | 'responseFilter' | 'excludeAnon'
  >
) => {
  const orgSelect = excludeAnon
    ? 'organization!inner(name)'
    : 'organization(name)'

  let quoteQuery = supabase
    .from('quote')
    .select(`id, name, public_id, org_id, created_at, ${orgSelect}`)
    .order('created_at', { ascending: false })

  if (excludeAnon) {
    quoteQuery = quoteQuery.neq('organization.name', 'Anon')
  }
  if (search?.trim()) {
    const orFilter = buildQuoteSearchOrFilter(search.trim())
    if (orFilter) quoteQuery = quoteQuery.or(orFilter)
  }

  const [{ data: quoteRows, error: quoteError }, activityByQuoteId] =
    await Promise.all([
      quoteQuery,
      fetchQuoteActivityByQuoteId(supabase),
    ])

  if (quoteError) throw quoteError

  const orderedIds = (quoteRows ?? [])
    .map((row) => {
      const activity = activityByQuoteId.get(row.id)
      return {
        id: row.id,
        activity,
        sortAt: activity?.lastMessageAt ?? row.created_at,
      }
    })
    .filter(({ activity }) => {
      if (responseFilter === 'all') return true
      return activity?.responseState === responseFilter
    })
    .sort((a, b) => {
      if (a.sortAt === b.sortAt) return 0
      return a.sortAt < b.sortAt ? 1 : -1
    })
    .map(({ id }) => id)

  return {
    orderedIds,
    totalCount: orderedIds.length,
  }
}

const fetchAdminRequestsOverviewDetails = async (
  supabase: SupabaseClient,
  {
    quoteIds,
    excludeAnon = true,
  }: {
    quoteIds: number[]
    excludeAnon?: boolean
  }
): Promise<QuoteListRow[]> => {
  if (quoteIds.length === 0) return []

  const orgSelect = excludeAnon
    ? 'organization!inner(name)'
    : 'organization(name)'

  const { data: detailRows, error: detailError } = await supabase
    .from('quote')
    .select(
      `id, name, public_id, status, org_id, created_at, ${orgSelect},
        quote_item(${QUOTE_LIST_ITEM_THUMBNAIL_FIELDS}),
        conversation(order_message(id))`
    )
    .in('id', quoteIds)

  if (detailError) throw detailError

  const detailById = new Map(
    ((detailRows ?? []) as QuoteListRow[]).map((row) => [row.id, row])
  )

  return quoteIds
    .map((id) => detailById.get(id))
    .filter((row): row is QuoteListRow => row != null)
}

/** Paginated admin requests overview with search + response filters. */
export const useAdminRequestsOverviewQuotes = ({
  page,
  pageSize,
  search,
  responseFilter = 'all',
  excludeAnon = true,
  enabled = true,
}: AdminRequestsOverviewParams) => {
  const supabase = useSupabaseClient()
  const isAdmin = useIsAdmin()

  const indexQuery = useQuery({
    queryKey: [
      queryKeys.quotes,
      'admin-requests-overview-index',
      search,
      responseFilter,
      excludeAnon,
    ],
    queryFn: () =>
      fetchAdminRequestsOverviewIndex(supabase, {
        search,
        responseFilter,
        excludeAnon,
      }),
    enabled: enabled && isAdmin,
    retry: false,
    staleTime: 30_000,
  })

  const pageIds = useMemo(() => {
    const orderedIds = indexQuery.data?.orderedIds ?? []
    const from = page * pageSize
    return orderedIds.slice(from, from + pageSize)
  }, [indexQuery.data?.orderedIds, page, pageSize])

  const pageQuery = useQuery({
    queryKey: [
      queryKeys.quotes,
      'admin-requests-overview-page',
      pageIds,
      excludeAnon,
    ],
    queryFn: () =>
      fetchAdminRequestsOverviewDetails(supabase, {
        quoteIds: pageIds,
        excludeAnon,
      }),
    enabled: enabled && isAdmin && indexQuery.isSuccess,
    retry: false,
    placeholderData: (previous) => previous,
  })

  const totalCount = indexQuery.data?.totalCount ?? 0
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize))

  return {
    data: {
      data: pageQuery.data ?? [],
      totalCount,
      page,
      pageSize,
      pageCount: totalCount === 0 ? 1 : pageCount,
    },
    isPending: indexQuery.isPending || pageQuery.isPending,
    isFetching: indexQuery.isFetching || pageQuery.isFetching,
  }
}

/** Cheap head-count for admin order list tabs. Uses estimated counts to avoid timeouts. */
export const useAdminQuoteSegmentCounts = () => {
  const supabase = useSupabaseClient()
  const isAdmin = useIsAdmin()

  return useQuery({
    queryKey: [queryKeys.quotes, 'admin-segment-counts'],
    queryFn: async () => {
      const [active, delivered] = await Promise.all([
        supabase
          .from('quote')
          .select('id', { count: 'estimated', head: true })
          .in('status', ACTIVE_QUOTE_STATUSES),
        supabase
          .from('quote')
          .select('id', { count: 'estimated', head: true })
          .eq('status', DELIVERED_STATUS),
      ])

      // Don't fail the page if counts time out — badges are optional
      return {
        active: active.error ? undefined : (active.count ?? 0),
        delivered: delivered.error ? undefined : (delivered.count ?? 0),
      }
    },
    enabled: isAdmin,
    retry: false,
  })
}

const QUOTE_ORG_SELECT = `organization(
  name,
  currency,
  org_number,
  vat_number,
  billing_email,
  billing_address_line1,
  billing_address_line2,
  billing_postcode,
  billing_city,
  billing_country,
  default_invoice_reference
)`

export const useQuoteById = (public_id: string | null | undefined) => {
  const supabase = useSupabaseClient()

  const quote = useQuery({
    queryKey: [queryKeys.quotes, 'byId', public_id],
    queryFn: async () => {
      if (!public_id) return null

      const { data, error } = await supabase
        .from('quote')
        .select(
          `*,
          ${QUOTE_ORG_SELECT},
          quote_item!quote_item_quote_id_fkey(
            id, quote_id, created_at, product_id, product_variant_color_id, quantity,
            design!design_quote_item_id_fkey(*),
            design_product_image(*),
            product:product_open!quote_item_product_id_fkey(id, price)
          ),
          ${QUOTE_PROOF_SELECT}
          `
        )
        .eq('public_id', public_id)
        .single()

      if (error) throw error

      return mapFetchedQuote(data as QuoteFetchRow)
    },
    enabled: !!public_id,
  })

  return quote
}

// Hook to fetch a single quote by public_id (for public viewing)
export const usePublicQuote = (public_id: string | null) => {
  const supabase = useSupabaseClient()

  const { data, isPending, error } = useQuery({
    queryKey: [queryKeys.quotes, 'public', public_id],
    queryFn: async () => {
      if (!public_id) return null

      const { data, error } = await supabase
        .from('quote')
        .select(
          `*,
          ${QUOTE_ORG_SELECT},
          quote_item!quote_item_quote_id_fkey(
            id, quote_id, created_at, product_id, product_variant_color_id, quantity,
            design!design_quote_item_id_fkey(*),
            design_product_image(*),
            product:product_open!quote_item_product_id_fkey(id, price)
          ),
          ${QUOTE_PROOF_SELECT}
          `
        )
        .eq('public_id', public_id)
        .single()

      if (error) throw error

      return mapFetchedQuote(data as QuoteFetchRow)
    },
    enabled: !!public_id,
  })

  return {
    quote: data,
    isLoading: isPending,
    error,
  }
}
